import { resolveKpiComparison } from "@utils/kpiComparison";
import api, { v2BaseUrl } from "../api";
import {
  FuelingRecord,
  FuelingSummary,
  fuelingSummaryMock,
  EfficiencyResponse,
  EfficiencyVehicle,
  TopVehicle,
} from "@components/Table/Fueling/mock";
import { buildAssetTreePayload } from "@utils/assetTreePayload";
import moment from "moment";

type TableDataFilters = {
  branchIds?: string[];
  segmentIds?: string[];
  clientIds?: string[];
  supplierIds?: string[];
  vehicleIds?: string[];
  driverIds?: string[];
  includeNullBranch?: boolean;
  includeNullSegment?: boolean;
  includeNullClient?: boolean;
  includeNullSupplier?: boolean;
  includeNullVehicle?: boolean;
  includeNullDriver?: boolean;
};

type TableDataQuery = {
  page?: number;
  size?: number;
};

export interface IFuelingPayload {
  transactionDate: string;
  vehicleId: string | null;
  driverIds: string[] | null;
  branchId: number;
  segmentBussinessId: number;
  supplierClientId: number;
  fuelType: string;
  fuelVolume: number;
  fuelUnitPrice: number;
  fuelTotalPrice: number;
  arla32Volume?: number;
  arla32UnitPrice?: number;
  arla32TotalPrice?: number;
  odometer?: number;
  stopLocation?: string;
  notes?: string;
}

const mapSummaryFromApi = (items: any[]): FuelingSummary => {
  const fuelTotals: Record<string, { quantity: number; expense: number }> = {
    biodiesel: { quantity: 0, expense: 0 },
    diesel: { quantity: 0, expense: 0 },
    etanol: { quantity: 0, expense: 0 },
    gasolina: { quantity: 0, expense: 0 },
    eletrico: { quantity: 0, expense: 0 },
    gnv: { quantity: 0, expense: 0 },
  };

  let totalFuelExpense = 0;
  let arla32Volume = 0;
  let arla32Expense = 0;

  items.forEach((item) => {
    const fuelType = (item.fuelType || "").toUpperCase();
    const volume = item.fuelVolume || 0;
    const expense = item.totalPaid || 0;

    totalFuelExpense += expense;

    if (fuelType.includes("BIODIESEL")) {
      fuelTotals.biodiesel.quantity += volume;
      fuelTotals.biodiesel.expense += expense;
    } else if (fuelType.includes("DIESEL")) {
      fuelTotals.diesel.quantity += volume;
      fuelTotals.diesel.expense += expense;
    } else if (fuelType.includes("ETANOL")) {
      fuelTotals.etanol.quantity += volume;
      fuelTotals.etanol.expense += expense;
    } else if (fuelType.includes("GASOLINA")) {
      fuelTotals.gasolina.quantity += volume;
      fuelTotals.gasolina.expense += expense;
    } else if (fuelType.includes("ELETRICO") || fuelType.includes("ELECTRIC")) {
      fuelTotals.eletrico.quantity += volume;
      fuelTotals.eletrico.expense += expense;
    } else if (fuelType.includes("GNV") || fuelType.includes("GAS")) {
      fuelTotals.gnv.quantity += volume;
      fuelTotals.gnv.expense += expense;
    }

    if (item.arlaVolume) {
      arla32Volume += item.arlaVolume;
      arla32Expense += item.arlaExpense || 0;
    }
  });

  const fuelTypes: FuelingSummary["fuelTypes"] = {};

  if (fuelTotals.biodiesel.quantity > 0 || fuelTotals.biodiesel.expense > 0) {
    fuelTypes.biodiesel = fuelTotals.biodiesel;
  }
  if (fuelTotals.diesel.quantity > 0 || fuelTotals.diesel.expense > 0) {
    fuelTypes.diesel = fuelTotals.diesel;
  }
  if (fuelTotals.etanol.quantity > 0 || fuelTotals.etanol.expense > 0) {
    fuelTypes.etanol = fuelTotals.etanol;
  }
  if (fuelTotals.gasolina.quantity > 0 || fuelTotals.gasolina.expense > 0) {
    fuelTypes.gasolina = fuelTotals.gasolina;
  }
  if (fuelTotals.eletrico.quantity > 0 || fuelTotals.eletrico.expense > 0) {
    fuelTypes.eletrico = fuelTotals.eletrico;
  }
  if (fuelTotals.gnv.quantity > 0 || fuelTotals.gnv.expense > 0) {
    fuelTypes.gnv = fuelTotals.gnv;
  }

  const quantityLiters =
    fuelTotals.biodiesel.quantity +
    fuelTotals.diesel.quantity +
    fuelTotals.etanol.quantity +
    fuelTotals.gasolina.quantity;

  const totalTypeExpense = Object.values(fuelTotals).reduce((sum, t) => sum + t.expense, 0);
  const dieselPercentage = totalTypeExpense > 0 ? Math.round((fuelTotals.diesel.expense / totalTypeExpense) * 100) : 0;
  const electricPercentage =
    totalTypeExpense > 0 ? Math.round((fuelTotals.eletrico.expense / totalTypeExpense) * 100) : 0;
  const gnvPercentage = totalTypeExpense > 0 ? Math.round((fuelTotals.gnv.expense / totalTypeExpense) * 100) : 0;

  return {
    quantityDiesel: quantityLiters,
    quantityElectric: fuelTotals.eletrico.quantity,
    quantityGNV: fuelTotals.gnv.quantity,
    arla32Volume,
    totalFuelings: items.length,
    fuelExpense: totalFuelExpense,
    fuelExpenseByType: {
      diesel: { value: fuelTotals.diesel.expense, percentage: dieselPercentage },
      electric: { value: fuelTotals.eletrico.expense, percentage: electricPercentage },
      gnv: { value: fuelTotals.gnv.expense, percentage: gnvPercentage },
    },
    fuelTypes,
    arla32Expense,
    totalExpense: totalFuelExpense + arla32Expense,
    totalExpenseVsPreviousMonthPercentage: 0,
    fuelTotalPriceVsPreviousMonthPercentage: 0,
    complementValueARLATotalVsPreviousMonthPercentage: 0,
    fleetVehicleTotal: 0,
    supplyTotal: items.length,
    fleetTotal: 0,
    averageFleetConsumption: 0,
    topVehiclesMostEconomical: [],
    topVehiclesLeastEconomical: [],
  };
};

export const getFuelingSummary = async (
  startDate: string,
  endDate: string,
  filters?: TableDataFilters
): Promise<FuelingSummary> => {
  const body: Record<string, any> = {
    startDate: moment(startDate).format("YYYY-MM-DD"),
    endDate: moment(endDate).format("YYYY-MM-DD"),
    assetTree: buildAssetTreePayload(filters),
  };

  const emptySummary: FuelingSummary = {
    quantityDiesel: 0,
    quantityElectric: 0,
    quantityGNV: 0,
    arla32Volume: 0,
    totalFuelings: 0,
    fuelExpense: 0,
    fuelExpenseByType: {
      diesel: { value: 0, percentage: 0 },
      electric: { value: 0, percentage: 0 },
      gnv: { value: 0, percentage: 0 },
    },
    fuelTypes: {},
    arla32Expense: 0,
    totalExpense: 0,
    totalExpenseVsPreviousMonthPercentage: 0,
    fuelTotalPriceVsPreviousMonthPercentage: 0,
    complementValueARLATotalVsPreviousMonthPercentage: 0,
    fleetVehicleTotal: 0,
    supplyTotal: 0,
    fleetTotal: 0,
    averageFleetConsumption: 0,
    topVehiclesMostEconomical: fuelingSummaryMock.topVehiclesMostEconomical ?? [],
    topVehiclesLeastEconomical: fuelingSummaryMock.topVehiclesLeastEconomical ?? [],
  };

  try {
    const response = await api.post(`${v2BaseUrl}/fuel-refills`, body);
    const root = response.data ?? {};
    const sum = root.sum ?? root.summary;
    const items = root.data ?? root.content ?? (Array.isArray(root) ? root : []);

    if (sum) {
      const sumPeriods = { currentPeriod: sum.currentPeriod, previousPeriod: sum.previousPeriod };
      const mapped = Array.isArray(items) && items.length > 0 ? mapSummaryFromApi(items) : emptySummary;
      return {
        ...mapped,
        totalExpense: sum.totalExpense ?? sum.totalPaid ?? mapped.totalExpense,
        fuelExpense: sum.fuelTotalPrice ?? sum.fuelUnitPriceAllFuelTotal ?? mapped.fuelExpense,
        arla32Expense: sum.complementValueARLATotal ?? mapped.arla32Expense,
        arla32Volume: sum.complementVolumeARLATotal ?? mapped.arla32Volume,
        totalFuelings: sum.supplyTotal ?? mapped.totalFuelings,
        totalExpenseVsPreviousMonthPercentage: sum.totalExpenseVsPreviousMonthPercentage ?? 0,
        fuelTotalPriceVsPreviousMonthPercentage: sum.fuelTotalPriceVsPreviousMonthPercentage ?? 0,
        complementValueARLATotalVsPreviousMonthPercentage: sum.complementValueARLATotalVsPreviousMonthPercentage ?? 0,
        fleetVehicleTotal: sum.fleetVehicleTotal ?? 0,
        supplyTotal: sum.supplyTotal ?? mapped.totalFuelings,
        fleetTotal: sum.fleetTotal ?? sum.fleetVehicleTotal ?? 0,
        averageFleetConsumption: sum.fleetAverageConsumption ?? 0,
        totalExpenseKpi: resolveKpiComparison(sum.totalExpenseKpi ?? sum.totalExpense, sumPeriods),
        arla32ExpenseKpi: resolveKpiComparison(
          sum.arla32ExpenseKpi ?? sum.complementValueARLATotalKpi ?? sum.complementValueARLATotal,
          sumPeriods
        ),
        fuelExpenseKpi: resolveKpiComparison(
          sum.fuelExpenseKpi ?? sum.fuelTotalPriceKpi ?? sum.fuelTotalPrice,
          sumPeriods
        ),
        averageFleetConsumptionKpi: resolveKpiComparison(
          sum.averageFleetConsumptionKpi ?? sum.fleetAverageConsumptionKpi ?? sum.fleetAverageConsumption,
          sumPeriods
        ),
        topVehiclesMostEconomical: sum.topVehiclesMostEconomical ?? fuelingSummaryMock.topVehiclesMostEconomical ?? [],
        topVehiclesLeastEconomical:
          sum.topVehiclesLeastEconomical ?? fuelingSummaryMock.topVehiclesLeastEconomical ?? [],
      };
    }

    if (Array.isArray(items) && items.length > 0) {
      return {
        ...emptySummary,
        ...mapSummaryFromApi(items),
      };
    }

    return emptySummary;
  } catch (error) {
    console.error("Error fetching fueling summary:", error);
    return emptySummary;
  }
};

const mapFuelTypeFromApi = (fuelType: string): string => {
  const typeMap: Record<string, string> = {
    DIESEL_S10: "Diesel S10",
    DIESEL_S500: "Diesel S500",
    DIESEL: "Diesel",
    BIODIESEL: "Biodiesel",
    ETANOL: "Etanol",
    GASOLINA: "Gasolina",
    GASOLINA_ADITIVADA: "Gasolina Aditivada",
    GNV: "GNV",
    ELETRICO: "Elétrico",
    ELECTRIC: "Elétrico",
  };
  return typeMap[fuelType.toUpperCase()] || fuelType;
};

const mapTransactionToFuelingRecord = (item: any, t?: (id: string) => string): FuelingRecord => {
  const supply = item.supplyTrucker;

  const fuelTypeRaw = supply?.fuelType || item.subcategoryName || "DIESEL";
  const fuelType = mapFuelTypeFromApi(fuelTypeRaw);
  const unit = supply?.fuelUnit || "";

  const dateTime = supply?.startDateTime || item.transactionDate || item.createdAt || new Date().toISOString();
  const formattedDate = moment(dateTime).format("DD/MM/YYYY - HH:mm");

  const driverName = item.driverName || t?.("fueling.driverNotInformed") || "Motorista não informado";
  const vehiclePlate = item.vehiclePlate || t?.("fueling.vehicleNotInformed") || "Veículo não informado";
  const location =
    supply?.stopLocation || item.branchName || t?.("fueling.locationNotInformed") || "Local não informado";
  const fuelVolume = supply?.fuelVolume ?? item.quantity ?? 0;
  const totalAmount = item.amount ?? supply?.totalPaid ?? supply?.fuelTotalPrice ?? 0;

  return {
    id: item.id?.toString() || "",
    date: formattedDate,
    driver: driverName,
    driverStatus: item.driverIds?.length > 0 || supply?.driverId ? "connected" : "team",
    vehicle: vehiclePlate,
    location: location,
    fuelType: fuelType as FuelingRecord["fuelType"],
    quantity: fuelVolume.toLocaleString("pt-BR", { minimumFractionDigits: 2 }),
    quantityUnit: unit,
    amount: totalAmount.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    }),
  };
};

export const getFuelingData = async (
  startDate: string,
  endDate: string,
  query?: TableDataQuery,
  filters?: TableDataFilters,
  t?: (id: string) => string
): Promise<{ content: FuelingRecord[]; totalElements: number }> => {
  const body: Record<string, any> = {
    type: "DESPESA",
    startDate: moment(startDate).format("YYYY-MM-DD"),
    endDate: moment(endDate).format("YYYY-MM-DD"),
    categoryName: "Abastecimento",
    assetTree: buildAssetTreePayload(filters),
  };

  if (typeof query?.page === "number") body.page = query.page;
  if (typeof query?.size === "number") body.size = query.size;

  try {
    const response = await api.post(`${v2BaseUrl}/transactions`, body);
    const payload = response?.data?.data ?? response?.data ?? response;
    const data = Array.isArray(payload?.content) ? payload.content : [];
    const totalElements = payload?.totalElements ?? payload?.total ?? data.length;

    const mappedData = data.map((item: any) => mapTransactionToFuelingRecord(item, t));

    return {
      content: mappedData,
      totalElements,
    };
  } catch (error) {
    console.error("Error fetching fueling data:", error);
    return {
      content: [],
      totalElements: 0,
    };
  }
};

export const getFuelingDetail = async (id: string): Promise<FuelingRecord> => {
  try {
    const response = await api.get(`/transactions/${id}`);
    return mapTransactionToFuelingRecord(response.data?.data ?? response.data);
  } catch (error) {
    console.error("Error fetching fueling detail:", error);
    throw error;
  }
};

export const createFueling = async (payload: IFuelingPayload, file?: File | null) => {
  const formData = new FormData();
  formData.append("transaction", JSON.stringify(payload));

  if (file) {
    formData.append("file", file);
  }

  const response = await api.post("/transactions", formData);
  return response.data?.data ?? response.data;
};

export const updateFueling = async (id: string, payload: IFuelingPayload, file?: File | null) => {
  if (!file) {
    const response = await api.put(`/transactions/${id}`, payload);
    return response.data?.data ?? response.data;
  }

  const formData = new FormData();
  formData.append("transaction", JSON.stringify(payload));
  formData.append("file", file);

  const response = await api.put(`/transactions/${id}`, formData);
  return response.data?.data ?? response.data;
};

export const deleteFueling = async (id: string) => {
  try {
    const response = await api.delete(`/transactions/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting fueling:", error);
    throw error;
  }
};

export const exportFueling = async (
  startDate: string,
  endDate: string,
  format: "xlsx" | "csv" = "xlsx"
): Promise<void> => {
  const params = {
    type: "DESPESA",
    categoryName: "Abastecimento",
    startDate: moment(startDate).format("YYYY-MM-DD"),
    endDate: moment(endDate).format("YYYY-MM-DD"),
    page: 0,
    size: 10,
  };

  try {
    const response = await api.get(`/transactions/export/supply/${format}`, {
      params,
      responseType: "blob",
    });

    const mimeType =
      format === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "text/csv";

    const blob = new Blob([response.data], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `ABASTECIMENTOS_${startDate}_${endDate}.${format}`);

    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error exporting fueling:", error);
    throw error;
  }
};

const FUEL_TYPE_MAP: Record<string, string> = {
  BIODIESEL: "Biodiesel",
  DIESEL_S10: "Diesel",
  DIESEL_S500: "Diesel",
  DIESEL: "Diesel",
  ELECTRIC: "Elétrico",
  GNV: "GNV",
  ETANOL: "Etanol",
  GASOLINA: "Gasolina",
};

const mapEfficiencyVehicleToTopVehicle = (vehicle: EfficiencyVehicle): TopVehicle => {
  const driverNames = vehicle.drivers.map((d) => d.name).join(", ");

  return {
    vehiclePlate: vehicle.vehicle.name,
    driverName: driverNames || "Sem motorista",
    fuelType: FUEL_TYPE_MAP[vehicle.fuelType] || vehicle.fuelType,
    consumptionUnit: vehicle.fuelUnit,
    consumptionValue: vehicle.fuelVolume,
    consumptionCurrency: vehicle.fuelTotalPrice,
  };
};

export const getFuelingEfficiency = async (
  limit: number = 5
): Promise<{ mostEconomic: TopVehicle[]; leastEconomic: TopVehicle[] }> => {
  try {
    const response = await api.get(`/fuel-refills/efficiency?limit=${limit}`);
    const data: Partial<EfficiencyResponse> = response.data?.data ?? response.data ?? {};

    return {
      mostEconomic: (data.mostEconomicVehicles || []).map(mapEfficiencyVehicleToTopVehicle),
      leastEconomic: (data.leastEconomicVehicles || []).map(mapEfficiencyVehicleToTopVehicle),
    };
  } catch (error) {
    console.error("Error fetching fueling efficiency:", error);
    return {
      mostEconomic: [],
      leastEconomic: [],
    };
  }
};

export interface AlertTotalsResponse {
  alerts: Record<string, number>;
  total: number;
}

export interface AlertHistoryApiItem {
  id: number;
  type: string;
  typeLabel: string;
  filterLabel: string;
  alertMessage: string;
  message: string;
  detail: string;
  occurredAt: string;
  driverName: string | null;
  vehiclePlate: string | null;
  vehicleName: string | null;
  rioAssetId: string | null;
  fuelVolume: number | null;
  tankCapacity: number | null;
  fuelUnitPrice: number | null;
  fleetAverageFuelUnitPrice: number | null;
  averageConsumption: number | null;
  fleetAverageConsumption: number | null;
}

export interface AlertHistoryApiResponse {
  content: AlertHistoryApiItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
}

export const getFuelingAlertHistory = async (
  types?: string[],
  page = 0,
  size = 20
): Promise<AlertHistoryApiResponse> => {
  try {
    const params = new URLSearchParams({ page: page.toString(), size: size.toString() });
    if (types?.length) params.set("types", types.join(","));

    const response = await api.get(`/fuel-refills/alerts/history?${params.toString()}`);
    const data = response.data?.data ?? response.data;
    return {
      content: data?.content ?? [],
      page: data?.page ?? 0,
      size: data?.size ?? size,
      totalElements: data?.totalElements ?? 0,
      totalPages: data?.totalPages ?? 0,
      first: data?.first ?? true,
      last: data?.last ?? true,
    };
  } catch (error) {
    console.error("Error fetching fueling alert history:", error);
    return { content: [], page: 0, size, totalElements: 0, totalPages: 0, first: true, last: true };
  }
};

export interface AlertDetailResponse {
  alertId: number;
  alertType: string;
  typeLabel: string;
  message: string;
  occurredAt: string | null;
  driverName: string | null;
  vehicle: {
    plate: string | null;
    model: string | null;
    rioAssetId: string | null;
  } | null;
  location: string | null;
  locationCoordinates: string | null;
  fuelType: string | null;
  volumeRefueled: number | null;
  tankCapacity: number | null;
  fuelUnitPrice: number | null;
  totalValue: number | null;
  fleetAverageFuelUnitPrice: number | null;
  fleetAveragePricePerLiter: number | null;
  averageConsumption: number | null;
  fleetAverageConsumption: number | null;
  recordedConsumption: number | null;
}

export const getFuelingAlertDetail = async (alertId: number): Promise<AlertDetailResponse | null> => {
  try {
    const response = await api.get(`/fuel-refills/alerts/${alertId}/detail`);
    return response.data?.data ?? response.data ?? null;
  } catch (error) {
    console.error("Error fetching fueling alert detail:", error);
    return null;
  }
};

export const getFuelingAlertTotals = async (): Promise<AlertTotalsResponse> => {
  try {
    const response = await api.get("/fuel-refills/alerts/totals");
    const data = response.data?.data ?? response.data;
    return {
      alerts: data?.alerts ?? {},
      total: data?.total ?? 0,
    };
  } catch (error) {
    console.error("Error fetching fueling alert totals:", error);
    return { alerts: {}, total: 0 };
  }
};
